import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Gift, Snowflake, Sun, Zap, GraduationCap, Moon, Crown, Check, Sparkles, Flame, Clock, Trophy } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LumenMascot } from "@/components/LumenMascot";
import { LoadingSkeleton, InlineError, EmptyState } from "@/components/states";
import { getRewards, redeemReward } from "@/lib/rewards.functions";
import { getWeeklySummary } from "@/lib/weekly.functions";
import { formatDate } from "@/lib/date-format";

export const Route = createFileRoute("/_app/rewards")({
  component: RewardsPage,
  head: () => ({
    meta: [
      { title: "Ricompense e riepilogo settimanale · Lumen" },
      { name: "description", content: "Riscatta ricompense con gli XP guadagnati studiando e consulta il riepilogo settimanale con progressi, streak e consigli." },
      { property: "og:title", content: "Ricompense e riepilogo settimanale · Lumen" },
      { property: "og:description", content: "Trasforma gli XP in ricompense e scopri i tuoi progressi della settimana." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

const ICONS: Record<string, typeof Gift> = {
  snowflake: Snowflake,
  sun: Sun,
  zap: Zap,
  graduation: GraduationCap,
  moon: Moon,
  crown: Crown,
};

function RewardsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const load = useServerFn(getRewards);
  const redeem = useServerFn(redeemReward);

  const q = useQuery({
    queryKey: ["rewards"],
    queryFn: () => load({ data: undefined } as never),
  });

  const m = useMutation({
    mutationFn: (key: string) => redeem({ data: { key } as never }),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(t("rewards.redeemed"));
        void qc.invalidateQueries({ queryKey: ["rewards"] });
      } else {
        toast.error(res.error === "owned" ? t("rewards.owned") : t("rewards.notEnough"));
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        tone="lilac"
        icon={<Gift className="h-5 w-5" />}
        kicker={t("rewards.title")}
        title={t("rewards.title")}
        subtitle={t("rewards.subtitle")}
      />

      {q.isLoading ? <LoadingSkeleton rows={4} /> : null}
      {q.isError ? <InlineError message={String(q.error)} onRetry={() => void q.refetch()} /> : null}

      {q.data ? (
        <>
          <Card className="tone-peach section-block border-0 p-4 shadow-none">
            <div className="flex items-center gap-3">
              <LumenMascot mood="celebrate" size={56} className="shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground/60">
                  {t("rewards.balance")}
                </p>
                <p className="text-2xl font-bold tabular-nums">{q.data.balance} XP</p>
                <p className="mt-1 text-xs text-foreground/70 break-words">
                  {t("rewards.total")}: {q.data.totalXp} · {t("rewards.spent")}: {q.data.spent}
                </p>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="shop">
            <TabsList className="w-full overflow-x-auto">
              <TabsTrigger value="shop" className="flex-1">{t("rewards.tabs.shop")}</TabsTrigger>
              <TabsTrigger value="history" className="flex-1">{t("rewards.tabs.history")}</TabsTrigger>
              <TabsTrigger value="weekly" className="flex-1">{t("rewards.tabs.weekly")}</TabsTrigger>
            </TabsList>

            <TabsContent value="shop" className="mt-3 grid gap-3 sm:grid-cols-2">
              {q.data.catalog.map((item) => {
                const Icon = ICONS[item.icon] ?? Gift;
                const pct = Math.min(100, Math.round((q.data.balance / item.cost) * 100));
                return (
                  <Card key={item.key} className="section-block border-0 p-4 shadow-none">
                    <div className="flex items-start gap-3">
                      <span className="tile-icon grid h-10 w-10 shrink-0 place-items-center">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold break-words">{t(`rewards.items.${item.key}.name`)}</p>
                        <p className="mt-0.5 text-sm text-muted-foreground break-words">
                          {t(`rewards.items.${item.key}.desc`)}
                        </p>
                        {!item.owned && !item.affordable ? (
                          <Progress value={pct} className="mt-2 h-1.5" />
                        ) : null}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <Badge variant="secondary" className="shrink-0">{item.cost} XP</Badge>
                          {item.owned ? (
                            <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
                              <Check className="h-4 w-4" /> {t("rewards.owned")}
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              className="rounded-full"
                              disabled={!item.affordable || m.isPending}
                              onClick={() => m.mutate(item.key)}
                            >
                              {item.affordable ? t("rewards.redeem") : t("rewards.notEnough")}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </TabsContent>

            <TabsContent value="history" className="mt-3">
              {q.data.history.length === 0 ? (
                <EmptyState title={t("rewards.tabs.history")} description={t("rewards.emptyHistory")} />
              ) : (
                <div className="space-y-2">
                  {q.data.history.map((h) => {
                    const Icon = ICONS[h.reward_key] ?? Sparkles;
                    return (
                      <Card key={h.id} className="section-block flex items-center gap-3 border-0 p-3 shadow-none">
                        <span className="tile-icon grid h-9 w-9 shrink-0 place-items-center">
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold break-words">
                            {t(`rewards.items.${h.reward_key}.name`)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(h.created_at, { day: "2-digit", month: "short" })}
                          </p>
                        </div>
                        <Badge variant="secondary" className="shrink-0">-{h.xp_cost} XP</Badge>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="weekly" className="mt-3">
              <WeeklyRecap />
            </TabsContent>
          </Tabs>
        </>
      ) : null}
    </div>
  );
}

function WeeklyRecap() {
  const { t, i18n } = useTranslation();
  const load = useServerFn(getWeeklySummary);
  const q = useQuery({ queryKey: ["weekly-summary"], queryFn: () => load({ data: undefined } as never) });

  if (q.isLoading) return <LoadingSkeleton rows={4} />;
  if (q.isError) return <InlineError message={String(q.error)} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const d = q.data;
  const maxMin = Math.max(30, ...d.days.map((x) => x.minutes));

  return (
    <div className="space-y-3">
      <Card className="tone-sky section-block border-0 p-4 shadow-none">
        <p className="text-xs font-semibold uppercase tracking-wider text-foreground/60">{t("rewards.weekly.title")}</p>
        <p className="text-sm text-foreground/70 break-words">
          {t("rewards.weekly.range", {
            from: formatDate(d.from, { day: "2-digit", month: "short" }),
            to: formatDate(d.to, { day: "2-digit", month: "short" }),
          })}
        </p>
        <div className="mt-3 flex items-end gap-1.5">
          {d.days.map((day) => (
            <div key={day.date} className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <div className="flex h-20 w-full items-end rounded-md bg-background/60">
                <div
                  className="w-full rounded-md bg-primary/70"
                  style={{ height: `${Math.round((day.minutes / maxMin) * 100)}%` }}
                />
              </div>
              <span className="text-xs text-foreground/60">
                {new Date(`${day.date}T00:00:00Z`).toLocaleDateString(i18n.language, { weekday: "narrow", timeZone: "UTC" })}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat icon={<Clock className="h-4 w-4" />} label={t("rewards.weekly.minutes")} value={`${d.totalMinutes}m`} hint={`${t("rewards.weekly.goal")}: ${d.goalMinutes}m`} />
        <Stat icon={<Sparkles className="h-4 w-4" />} label={t("rewards.weekly.xp")} value={String(d.xpWeek)} />
        <Stat icon={<Trophy className="h-4 w-4" />} label={t("rewards.weekly.quests")} value={String(d.questsDone)} />
        <Stat icon={<Flame className="h-4 w-4" />} label={t("rewards.weekly.streak")} value={String(d.streak)} />
        <Stat icon={<Check className="h-4 w-4" />} label={t("rewards.weekly.activeDays")} value={`${d.activeDays}/7`} hint={`${t("rewards.weekly.protected")}: ${d.protectedDays}`} />
        {d.quizAvg !== null ? (
          <Stat icon={<GraduationCap className="h-4 w-4" />} label={t("rewards.weekly.quizAvg")} value={`${d.quizAvg}%`} />
        ) : null}
      </div>

      <Card className="tone-sage section-block border-0 p-4 shadow-none">
        <div className="flex items-start gap-3">
          <LumenMascot mood="happy" size={48} className="shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold break-words">{t("rewards.weekly.tips")}</p>
            {d.tips.length === 0 ? (
              <p className="mt-1 text-sm text-foreground/70 break-words">{t("rewards.weekly.noTips")}</p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {d.tips.map((tip, i) => (
                  <li key={i} className="text-sm break-words">• {tip}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Stat({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
  return (
    <Card className="section-block border-0 p-3 shadow-none">
      <div className="flex items-center gap-2 text-foreground/60">
        {icon}
        <span className="truncate text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-1 text-lg font-bold tabular-nums">{value}</p>
      {hint ? <p className="text-xs text-foreground/60 break-words">{hint}</p> : null}
    </Card>
  );
}
